import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MailWarning, RefreshCw, CheckCircle2, Search } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EmailCollectionPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/email-collection?limit=1000`, { withCredentials: true });
            setData(data);
        } catch {
            toast.error('Failed to load email report');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const pct = data?.percent_real ?? 0;
    const players = (data?.placeholder_players || []).filter((p) => {
        if (!q.trim()) return true;
        const s = q.trim().toLowerCase();
        return (p.name || '').toLowerCase().includes(s) || (p.phone || '').includes(s);
    });

    return (
        <div className="card-dark p-5 mb-4" style={{ borderLeft: '3px solid #FFB020' }} data-testid="email-collection-panel">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <MailWarning className="w-5 h-5 text-[#FFB020]" />
                    <h3 className="text-lg font-bold text-white">Real-Email Adoption</h3>
                </div>
                <Button size="sm" variant="outline" onClick={load}
                    className="h-8 border-white/10 text-[#A0AAB2] hover:text-white" data-testid="email-report-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                </Button>
            </div>
            <p className="text-xs text-[#A0AAB2] mb-4">
                Players on a <span className="text-[#FFB020] font-mono">@betohalics.app</span> placeholder can't receive email.
                They're prompted to add a real one on next login.
            </p>

            {data && (
                <>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="rounded-lg bg-[#0a0a0a] border border-white/10 p-3 text-center" data-testid="email-stat-total">
                            <p className="text-2xl font-black text-white font-mono">{data.total_players}</p>
                            <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Players</p>
                        </div>
                        <div className="rounded-lg bg-[#39FF14]/5 border border-[#39FF14]/30 p-3 text-center" data-testid="email-stat-real">
                            <p className="text-2xl font-black text-[#39FF14] font-mono">{data.real_email_count}</p>
                            <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Real email</p>
                        </div>
                        <div className="rounded-lg bg-[#FFB020]/5 border border-[#FFB020]/30 p-3 text-center" data-testid="email-stat-placeholder">
                            <p className="text-2xl font-black text-[#FFB020] font-mono">{data.placeholder_count}</p>
                            <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Placeholder</p>
                        </div>
                    </div>

                    <div className="mb-4">
                        <div className="flex items-center justify-between text-[11px] mb-1">
                            <span className="text-[#A0AAB2] font-semibold">Adoption</span>
                            <span className="text-[#39FF14] font-bold">{pct}% have a real email</span>
                        </div>
                        <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-[#39FF14] to-[#FFD700] transition-all"
                                style={{ width: `${pct}%` }} data-testid="email-adoption-bar" />
                        </div>
                    </div>

                    {data.placeholder_count === 0 ? (
                        <div className="flex items-center gap-2 text-[#39FF14] text-sm py-3" data-testid="email-all-done">
                            <CheckCircle2 className="w-4 h-4" /> Every player has a real email on file. 🎉
                        </div>
                    ) : (
                        <>
                            <div className="relative mb-2">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0AAB2]" />
                                <Input value={q} onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search name or phone"
                                    className="pl-9 h-9 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                    data-testid="email-report-search" />
                            </div>
                            <div className="max-h-72 overflow-y-auto rounded-lg border border-white/10" data-testid="email-placeholder-list">
                                {players.map((p) => (
                                    <div key={p.id} className="flex items-center justify-between px-3 py-2 border-b border-white/5 last:border-0"
                                        data-testid={`email-placeholder-row-${p.id}`}>
                                        <span className="text-white text-sm truncate">{p.name || 'Player'}</span>
                                        <span className="text-[#A0AAB2] font-mono text-xs">{p.phone || '—'}</span>
                                    </div>
                                ))}
                                {players.length === 0 && (
                                    <p className="text-xs text-[#A0AAB2] italic p-3 text-center">No matches.</p>
                                )}
                            </div>
                            {data.capped && (
                                <p className="text-[10px] text-[#A0AAB2] mt-2 italic">
                                    Showing the first {data.limit} — {data.placeholder_count} total on placeholders.
                                </p>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
